// Reports › Live in Founder OS (FOUNDER-OS phase 1, design §3.1): the live picture across the
// portfolio's companies, one `GET portfolio/live`, blocks top to bottom — Alerts (every company's
// needs-you chips, the open head seats and stale numbers, newest first), Health, KPIs vs targets,
// Money (budget vs actual per company and a total per currency; cash needs an accounting
// connection, none yet), Workforce and Priorities. Every company name opens its company page.
// Empty states say what is true ("No companies yet", "No numbers yet"); nothing is made up.
import {
  Badge,
  LocationHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ActivityIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { PortfolioLive, PortfolioMoney } from "../../api/types";
import { HealthDot, SwitcherTile } from "../../shell/company-switcher";
import type { CompanyAccess } from "../company";
import { formatHours, formatMoney, formatPercent, formatRecent, formatScore } from "../format";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { formatSpend, TrendArrow, Withheld } from "./portfolio-parts";
import { portfolioLiveQuery } from "./portfolio-queries";

const ALERT_TONE: Record<string, "danger" | "warning" | "info"> = {
  kpi: "danger",
  escalation: "danger",
  budget: "danger",
  priority: "warning",
  meeting: "warning",
  vacancy: "warning",
  stale: "warning",
  approvals: "info",
};

export function PortfolioLiveScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const live = useQuery(portfolioLiveQuery(api, company.id));
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-live">
      <LocationHeader
        icon={ActivityIcon}
        crumbs={[{ label: "Reports" }]}
        title="Live"
        {...(live.data ? { count: live.data.health.length } : {})}
      />
      <div className="flex min-w-0 flex-col gap-6 px-6 py-5 max-md:px-4">
        {live.isPending ? (
          <Loading label="Loading the live report" rows={4} />
        ) : live.isError ? (
          <LoadError what="The live report" error={live.error} />
        ) : live.data.health.length === 0 ? (
          <Nothing title="No companies yet">
            Add a company from Portfolio › Focus; its numbers show here as they arrive.
          </Nothing>
        ) : (
          <LiveBlocks slug={company.slug} data={live.data} />
        )}
      </div>
    </div>
  );
}

function Block({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={`live-${id}`} data-block={id} className="flex flex-col gap-2">
      <h2 id={`live-${id}`} className="font-heading text-base font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

function CompanyLink({ slug, id, name }: { slug: string; id: string; name: string }) {
  return (
    <Link
      to="/c/$slug/portfolio/companies/$companyId"
      params={{ slug, companyId: id }}
      className="inline-flex min-w-0 items-center gap-2 font-medium text-foreground hover:underline"
    >
      <SwitcherTile name={name} portfolio={false} />
      <span className="truncate">{name}</span>
    </Link>
  );
}

const none = <span className="text-muted-foreground">No numbers yet</span>;
const money = (m: PortfolioMoney, n: number | null) =>
  n === null ? "—" : formatMoney(n, m.currency, { whole: true });

function LiveBlocks({ slug, data }: { slug: string; data: PortfolioLive }) {
  const co = (r: { company_id: string; name: string }) => (
    <CompanyLink slug={slug} id={r.company_id} name={r.name} />
  );
  const workforce = data.workforce.rows.filter((r) => !r.withheld);
  const sum = (k: keyof (typeof workforce)[number]) =>
    workforce.reduce((n, r) => n + (r[k] as number), 0);
  return (
    <>
      <Block id="alerts" title="Alerts">
        {data.alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing needs you right now.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
            {data.alerts.map((a, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: alerts have no id; kind and company repeat
                key={`${a.company_id}:${a.kind}:${i}`}
                data-alert={a.kind}
                className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
              >
                {co(a)}
                <Badge variant={ALERT_TONE[a.kind] ?? "info"} dot>
                  {a.text}
                </Badge>
                {a.at ? (
                  <span className="ml-auto text-xs text-faint">{formatRecent(a.at)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block id="health" title="Health">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Completeness</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.health.map((r) => (
              <TableRow key={r.company_id} data-company={r.slug}>
                <TableCell>{co(r)}</TableCell>
                <TableCell>
                  {r.withheld ? (
                    <Withheld name={r.name} />
                  ) : r.kpi_score === null && r.band === null ? (
                    none
                  ) : (
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <HealthDot band={r.band} />
                      {formatScore(r.kpi_score)}
                      <TrendArrow trend={r.trend} />
                    </span>
                  )}
                </TableCell>
                <TableCell className="tabular-nums">
                  {r.completeness === null ? "—" : formatPercent(r.completeness)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {r.updated_at ? formatRecent(r.updated_at) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block id="kpis" title="KPIs vs targets">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Hit</TableHead>
              <TableHead>Missed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.kpis.map((r) => (
              <TableRow key={r.company_id} data-company={r.slug}>
                <TableCell>{co(r)}</TableCell>
                {r.sharing === "withheld" ? (
                  <TableCell colSpan={2}>
                    <Withheld name={r.name} />
                  </TableCell>
                ) : r.sharing === "scores" ? (
                  <TableCell colSpan={2} className="text-muted-foreground">
                    Scores only
                  </TableCell>
                ) : r.hit + r.miss === 0 ? (
                  <TableCell colSpan={2}>{none}</TableCell>
                ) : (
                  <>
                    <TableCell className="tabular-nums">{r.hit}</TableCell>
                    <TableCell>
                      <span className="tabular-nums">{r.miss}</span>
                      {r.misses.length ? (
                        <span className="text-muted-foreground"> · {r.misses.join(", ")}</span>
                      ) : null}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Block>

      <Block id="money" title="Money">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Budget</TableHead>
              <TableHead>Actual</TableHead>
              <TableHead>Variance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.money.rows.map((r) => (
              <TableRow key={r.company_id} data-company={r.slug}>
                <TableCell>{co(r)}</TableCell>
                {r.withheld ? (
                  <TableCell colSpan={3}>
                    <Withheld name={r.name} />
                  </TableCell>
                ) : !r.money ? (
                  <TableCell colSpan={3}>{none}</TableCell>
                ) : (
                  <>
                    <TableCell className="tabular-nums">
                      {money(r.money, r.money.budget_minor)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {money(r.money, r.money.actual_minor)}
                    </TableCell>
                    <TableCell
                      className={
                        (r.money.variance_minor ?? 0) < 0
                          ? "text-danger-text tabular-nums"
                          : "tabular-nums"
                      }
                    >
                      {money(r.money, r.money.variance_minor)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {data.money.totals.map((t) => (
              <TableRow key={t.currency} data-slot="money-total" className="font-medium">
                <TableCell>Total ({t.currency})</TableCell>
                <TableCell className="tabular-nums">{money(t, t.budget_minor)}</TableCell>
                <TableCell className="tabular-nums">{money(t, t.actual_minor)}</TableCell>
                <TableCell className="tabular-nums">{money(t, t.variance_minor)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.money.cash_connected ? null : (
          <p
            data-slot="cash-empty"
            className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
          >
            <span className="font-medium text-secondary">Cash: connect accounting.</span> Cash needs
            an accounting or bank connection, which comes later; until then money is budget against
            actual.
          </p>
        )}
      </Block>

      <Block id="workforce" title="Workforce">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>People</TableHead>
              <TableHead>DEs live</TableHead>
              <TableHead>AGs</TableHead>
              <TableHead>Runs 7d</TableHead>
              <TableHead>Cost this month</TableHead>
              <TableHead>Hours this week</TableHead>
              <TableHead>Approvals</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.workforce.rows.map((r) => (
              <TableRow key={r.company_id} data-company={r.slug}>
                <TableCell>{co(r)}</TableCell>
                {r.withheld ? (
                  <TableCell colSpan={7}>
                    <Withheld name={r.name} />
                  </TableCell>
                ) : (
                  <>
                    <TableCell className="tabular-nums">{r.people}</TableCell>
                    <TableCell className="tabular-nums">
                      {r.des_live}/{r.des}
                    </TableCell>
                    <TableCell className="tabular-nums">{r.ags}</TableCell>
                    <TableCell className="tabular-nums">
                      {r.runs_7d}
                      {r.failed_7d ? (
                        <span className="text-danger-text"> · {r.failed_7d} failed</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatSpend(r.spend_month_micros)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatHours(r.hours_week_minutes)}
                    </TableCell>
                    <TableCell className="tabular-nums">{r.approvals_waiting}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
            {workforce.length > 1 ? (
              <TableRow data-slot="workforce-total" className="font-medium">
                <TableCell>Total</TableCell>
                <TableCell className="tabular-nums">{sum("people")}</TableCell>
                <TableCell className="tabular-nums">
                  {sum("des_live")}/{sum("des")}
                </TableCell>
                <TableCell className="tabular-nums">{sum("ags")}</TableCell>
                <TableCell className="tabular-nums">{sum("runs_7d")}</TableCell>
                <TableCell className="tabular-nums">
                  {formatSpend(sum("spend_month_micros"))}
                </TableCell>
                <TableCell className="tabular-nums">
                  {formatHours(sum("hours_week_minutes"))}
                </TableCell>
                <TableCell className="tabular-nums">{sum("approvals_waiting")}</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Block>

      <Block id="priorities" title="Priorities">
        <Link
          to="/c/$slug/kpis/priorities"
          params={{ slug }}
          className="flex w-fit items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm shadow-xs hover:bg-muted"
        >
          <span className="font-medium text-foreground">Portfolio priorities</span>
          <span className="text-muted-foreground">{data.priorities.portfolio.quarter}</span>
          <Badge variant="success">{data.priorities.portfolio.on} on track</Badge>
          {data.priorities.portfolio.at_risk ? (
            <Badge variant="warning">{data.priorities.portfolio.at_risk} at risk</Badge>
          ) : null}
          {data.priorities.portfolio.off ? (
            <Badge variant="danger">{data.priorities.portfolio.off} off</Badge>
          ) : null}
        </Link>
        {data.priorities.off_track.length === 0 ? (
          <p className="text-sm text-muted-foreground">No company priority is off track.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
            {data.priorities.off_track.map((p) => (
              <li key={p.goal_id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                {co(p)}
                <span className="min-w-0 flex-1 truncate">{p.title}</span>
                <Badge variant={p.track === "off" ? "danger" : "warning"}>
                  {p.track === "off" ? "Off track" : "At risk"}
                </Badge>
                {p.owner_name ? (
                  <span className="text-xs text-muted-foreground">{p.owner_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Block>
    </>
  );
}
